import { useCallback, useEffect, useState } from "react";
import { createPeopleSearch } from "./peopleSearchCore.js";

let pending = null;

export function loadSearchCorpus() {
  if (pending) return pending;

  pending = Promise.all([
    import("../data/peopleIndex/peopleIndex.json"),
    import("../data/battles/battleData.json"),
    import("../data/generated/searchRecords.json"),
  ])
    .then(([peopleModule, battleModule, recordModule]) => {
      const people = peopleModule.default || peopleModule;
      const battles = battleModule.default || battleModule;
      const records = recordModule.default || recordModule;

      return {
        ...createPeopleSearch(Array.isArray(people) ? people : []),
        battles: Array.isArray(battles) ? battles : [],
        records: Array.isArray(records) ? records : [],
      };
    })
    .catch(() => {
      /* Let the next keystroke try again rather than failing for the session. */
      pending = null;
      return null;
    });

  return pending;
}

/**
 * Returns the corpus once it has arrived, and a `prime` to start fetching.
 *
 * Prime on focus rather than on the first keystroke: the request then overlaps
 * the time a reader spends typing, so by the second character it is usually
 * already here. Until it arrives `corpus` is null and the caller shows what it
 * can -- which is why page results are kept in the main bundle.
 */
export function useSearchCorpus() {
  const [corpus, setCorpus] = useState(null);

  /* Priming has to move state, or the effect below has no reason to re-run and
     the fetch it started would never be collected. */
  const [primed, setPrimed] = useState(() => Boolean(pending));

  const prime = useCallback(() => {
    loadSearchCorpus();
    setPrimed(true);
  }, []);

  useEffect(() => {
    if (!primed || corpus) return undefined;

    let cancelled = false;

    loadSearchCorpus().then((loaded) => {
      if (!cancelled && loaded) setCorpus(loaded);
    });

    return () => {
      cancelled = true;
    };
  }, [primed, corpus]);

  return { corpus, prime };
}

/**
 * Matches the records that are not pages -- events, psalms, letters, eras,
 * manuscripts, nations.
 *
 * Scored like siteSearch scores pages, so a record and a page competing for
 * the same query rank on comparable terms. Grouped by the `group` the
 * generator assigned, because 820 undifferentiated rows would bury the people
 * and battles a reader is usually after.
 */
export function matchRecords(records, query, limitPerGroup = 4) {
  const normalized = normalizeQuery(query);
  if (!normalized || !Array.isArray(records)) return [];

  const words = normalized.split(" ").filter(Boolean);
  const scored = [];

  for (const record of records) {
    const title = normalizeQuery(record.title);
    let score = 0;

    if (title === normalized) score += 100;
    else if (title.startsWith(normalized)) score += 50;
    else if (title.includes(normalized)) score += 35;

    for (const keyword of record.keywords || []) {
      const value = normalizeQuery(keyword);
      if (value === normalized) score += 30;
      else if (value.includes(normalized)) score += 12;
    }

    if (!score) {
      /* Every word present still counts, so "psalm trust" finds Psalm 23. */
      const haystack = `${title} ${(record.keywords || []).join(" ")}`.toLowerCase();
      if (words.length > 1 && words.every((word) => haystack.includes(word))) {
        score += 10;
      }
    }

    if (score > 0) scored.push({ ...record, score });
  }

  scored.sort((a, b) => b.score - a.score || a.title.localeCompare(b.title));

  /* Cap per group so one large source cannot crowd out the others. */
  const perGroup = new Map();
  const kept = [];

  for (const record of scored) {
    const taken = perGroup.get(record.group) || 0;
    if (taken >= limitPerGroup) continue;

    perGroup.set(record.group, taken + 1);
    kept.push(record);
  }

  return kept;
}

function normalizeQuery(value = "") {
  return String(value || "")
    .toLowerCase()
    .trim()
    .replace(/[\u2019']/g, "")
    .replace(/[^a-z0-9\s-]/g, " ")
    .replace(/\s+/g, " ");
}
